import React, { useEffect } from 'react';
import { Button } from 'src/components/ui/button';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { format } from 'date-fns';
import { useOpportunityHistory } from 'src/services/api/queries/opportunityQueries';
import { useToast } from 'src/hooks/use-toast';

// Timeline Item Component
const TimelineItem = ({ item, isLast }) => {
  const sourceLabel = item?.source
    ? item.source.charAt(0).toUpperCase() + item.source.slice(1)
    : null;

  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-2 h-2 rounded-full bg-primary mt-0" />
        {!isLast && <div className="w-[2px] h-full bg-border" />}
      </div>
      <div className="pb-6">
        <div className="text-sm text-muted-foreground">
          {item.timestamp
            ? format(new Date(item.timestamp), "MMM d, yyyy 'at' h:mm a")
            : '—'}
        </div>
        <div className="mt-1 space-y-1">
          <div className="text-sm font-medium text-foreground-strong">
            {item.action}
          </div>
          <div className="text-xs text-muted-foreground">
            {item.name || 'Unknown user'}
            {sourceLabel ? ` • ${sourceLabel}` : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const HistoryModal = ({ isOpen, onClose, opportunityId }) => {
  const { toast } = useToast();
  const {
    data: historyData,
    isLoading,
    error,
    refetch,
  } = useOpportunityHistory(opportunityId);

  useEffect(() => {
    if (isOpen) {
      refetch();
    }
  }, [isOpen, refetch]);

  useEffect(() => {
    if (error) {
      toast({
        message: error.message || 'Failed to fetch history',
        variant: 'destructive',
      });
    }
  }, [error, toast]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      );
    }

    const historyItems = historyData?.data
      ? [...historyData.data].sort(
          (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
        )
      : [];

    if (!historyItems.length) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No history available
        </div>
      );
    }

    return (
      // DialogBody is already the scroll container (overflow-y-auto
      // max-h-[64vh]) — adding a second overflow-y-auto here nested a
      // scrollable region inside a scrollable region, so the dialog showed
      // two scrollbars stacked on top of each other.
      <div className="space-y-0">
        {historyItems.map((item, index) => (
          <TimelineItem
            key={item.id || item.timestamp}
            item={item}
            isLast={index === historyItems.length - 1}
          />
        ))}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Opportunity History</DialogTitle>
          <DialogDescription>
            Timeline of status changes and actions for this opportunity.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">{renderContent()}</DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryModal;
